import { useModal } from "@/components/Modal/ModalStackManager";
import styled from "@emotion/styled";
import { CalendarLevel, DatePicker, DateValue, DatesRangeValue } from "@mantine/dates";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useState } from "react";

export type CalendarDate = [Date | null, Date | null];

export interface CalendarProps {
  dates: CalendarDate;
  type?: "range" | "default";
  onChange?: (dates: CalendarDate) => void; // 현재 'range' 타입에서만 사용, 날짜 범위 선택 시 호출. 부모 컴포넌트의 onChange를 수행
  size?: "xs" | "sm" | "md" | "lg" | "xl"; // 기본적으로는 'xl'로 설정.
  defaultLevel?: CalendarLevel;
  maxDate?: boolean;
}

export const isCalendarDate = (value: any): value is CalendarDate => {
  // 첫째, value가 배열인지 확인
  if (!Array.isArray(value) || value.length !== 2) {
    return false;
  }
  // 둘째, 배열의 각 요소가 Date 인스턴스이거나 null인지 확인
  const checkDateOrNull = (item: any) => item === null || item instanceof Date;
  return checkDateOrNull(value[0]) && checkDateOrNull(value[1]);
};

export const Calendar = ({
  dates,
  type = "default",
  onChange,
  size,
  defaultLevel = "month",
  maxDate,
}: CalendarProps) => {
  const { closeModal } = useModal();
  // 단일 날짜 선택 혹은 날짜 범위 선택에 따른 초기 값 설정
  const [value, setValue] = useState<DatesRangeValue | DateValue>(
    type === "range" ? dates : dates[0],
  );

  const handleChange = (selectedDates: DatesRangeValue | DateValue) => {
    setValue(selectedDates);
    // 'range' 타입: 날짜 범위 선택 로직 / 'default' 타입: 단일 날짜 선택 로직
    if (type === "range" && Array.isArray(selectedDates) && selectedDates[1] !== null) {
      onChange ? onChange(selectedDates) : closeModal(selectedDates);
    } else if (type === "default") {
      if (!Array.isArray(selectedDates) && selectedDates !== null) {
        onChange
          ? onChange([selectedDates, selectedDates])
          : closeModal([selectedDates, selectedDates]);
      }
      // type이 default지만, defaultLevel이 'year' 또는 'decade'인 경우, 해당 레벨의 시작일과 종료일을 선택
      else if (Array.isArray(selectedDates) && selectedDates[0] !== null) {
        onChange ? onChange(selectedDates) : closeModal(selectedDates);
      }
    }
  };

  return (
    <MyDatePicker
      type={type === "range" ? "range" : "default"}
      defaultLevel={defaultLevel}
      allowSingleDateInRange={type === "range"}
      firstDayOfWeek={0}
      value={value}
      onChange={handleChange}
      size={size ?? "xl"}
      previousIcon={<IconChevronLeft color="black" />}
      nextIcon={<IconChevronRight color="black" />}
      locale="ko"
      maxDate={maxDate ? dayjs().endOf("month").toDate() : undefined}
      monthLabelFormat="YYYY년 MM월"
      onMonthSelect={(month) => {
        if (defaultLevel === "year") {
          const start = dayjs(month).startOf("month").toDate();
          const end = dayjs(month).endOf("month").toDate();
          handleChange([start, end]);
        }
      }}
      onYearSelect={(year) => {
        if (defaultLevel === "decade") {
          const start = dayjs(year).startOf("year").toDate();
          const end = dayjs(year).endOf("year").toDate();
          handleChange([start, end]);
        }
      }}
    />
  );
};

const MyDatePicker = styled(DatePicker)`
  .mantine-DatePicker-monthCell:last-child > button[data-weekend="true"] {
    color: blue;
  }
`;
